/* eslint-disable react/prop-types */
/**
 * PaymentOverlay — Settle in two keystrokes (Interaction Refinement).
 *
 * The overlay opens as a compact PROMPT dialog. Cash is pre-selected,
 * payable is shown large, no keypad is visible, and the hint states
 * "Enter tenders ₹XXX in Cash." The cashier can:
 *
 *   Enter          → tenders the exact payable against the selected mode
 *   Enter (again)  → completes the payment (ORDER_SETTLED)
 *
 * On an exact-amount sale the second Enter also closes the overlay
 * immediately and runs the post-settle orchestration — no
 * confirmation panel, no timer.
 *
 * Change-due path (cash tendered > payable) transitions the overlay
 * in place to the settlement-complete panel that was already built:
 * invoice number, tender breakdown, change due as the LARGEST figure.
 * Enter there fires Done, which closes and orchestrates.
 *
 * Typing any digit expands the keypad + quick-tender area for
 * partial and split tenders. This is the exception path, not the
 * default layout.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Banknote, CreditCard, Smartphone, Gift, Building, HeartHandshake, Wallet,
  Check, X as XIcon, CheckCircle2, CornerDownLeft, Delete, Utensils, QrCode,
  RotateCcw,
} from "lucide-react";
import { ACT, ORDER_STATE, computeTotals, getActive } from "./posReducer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const fmtRupee = (n) => "₹" + fmt(n);

const MODES = [
  { id: "cash",          label: "Cash",           icon: Banknote,       allowsQuick: true  },
  { id: "card",          label: "Card",           icon: CreditCard,     allowsQuick: false },
  { id: "upi",           label: "UPI",            icon: Smartphone,     allowsQuick: false },
  // Part 2 (Sprint 3) · Gift voucher removed as a payment mode. It is
  // a redemption source in the discount dialog so voucher value
  // reduces taxable value BEFORE tax is computed (correct GST base).
  { id: "room_posting",  label: "Room posting",   icon: Building,       allowsQuick: false },
  { id: "complimentary", label: "Complimentary",  icon: HeartHandshake, allowsQuick: false },
  { id: "due_credit",    label: "Due",            icon: Wallet,         allowsQuick: false },
  // Part 4.1 · aggregators added as first-class settlement modes.
  { id: "swiggy_dineout",label: "Swiggy Dine Out",icon: Utensils,       allowsQuick: false },
  { id: "zomato",        label: "Zomato",         icon: Utensils,       allowsQuick: false },
  // Part 4.4 · guest-paid online link (pending → auto-settle on confirm).
  { id: "pay_online",    label: "Pay online",     icon: QrCode,         allowsQuick: false },
];

// Part 4.2 · reference labels + required flag per mode. Cash / gift
// voucher / complimentary need nothing. Pay online generates a code
// on tender and doesn't require a manual reference.
const REFERENCE_LABEL = {
  card:           "Card auth code",
  upi:            "UPI txn ref",
  room_posting:   "Room number",
  swiggy_dineout: "Swiggy order ref",
  zomato:         "Zomato order ref",
  due_credit:     "Client / party name",
};

// Sprint 5 correction · minimum reference length. Enter refuses to
// accept anything shorter. Card / UPI / aggregators need 4 chars —
// short enough to fit the smallest real auth code, long enough to
// reject accidental single-key presses. Room number and due-credit
// stay unrestricted (a room can be "3"; a party name has no fixed
// length).
const MIN_REF_LEN = {
  card:           4,
  upi:            4,
  swiggy_dineout: 4,
  zomato:         4,
  room_posting:   1,
  due_credit:     1,
};

const QUICK_CASH = [500, 1000, 2000];

export default function PaymentOverlay({ open, state, dispatch, livePos, requestAuth, onCancel, onDone }) {
  const active   = getActive(state) || { order: {}, lines: [], payments: [] };
  const totals   = useMemo(() => computeTotals(state), [state]);
  const [mode,   setMode]   = useState("cash");
  const [amount, setAmount] = useState("");         // empty = "use exact outstanding balance"
  const [reference, setReference] = useState("");   // Part 9 · required for card/upi/room
  // Sprint 5 correction · Enter-chain state.
  // `pendingRefTenderId` records the id of the just-created tender
  // whose reference the cashier is about to type. The reference
  // field, once populated to the min length and confirmed by Enter,
  // fires PAYMENT_REFERENCE_UPDATED against this id and (if balance
  // is zero) completes the settlement.
  const [pendingRefTenderId, setPendingRefTenderId] = useState(null);
  const refInputRef = useRef(null);
  // Financial Ground Truth (Feb 2026) · payment lock. Once ANY
  // settlement-touching request is in flight (add tender, settle,
  // reverse, accept-reference), the overlay refuses further tenders /
  // settle presses / Enter presses. The lock is set BEFORE the
  // request is dispatched and released only in the promise's finally
  // block, so success and error paths both release. Buttons AND the
  // keyboard handler consult the same guard.
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);   // for the window-keydown listener
  const withLock = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      return await fn();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  // Part 1 (Sprint 3) · amount field + keypad are visible by default.
  // The compact-prompt path has been removed so partial and split
  // tenders are reachable without discovering a hidden interaction.
  const [expanded, setExpanded] = useState(true);
  const containerRef = useRef(null);
  const doneRef      = useRef(null);

  // Sprint 5 correction · minimum reference length for the mode
  // currently selected. When no manual reference is required this is
  // 0 so the Enter guard passes.
  const referenceLabel   = REFERENCE_LABEL[mode] || null;
  const referenceRequired = !!referenceLabel;
  const minRefLen        = referenceRequired ? (MIN_REF_LEN[mode] || 1) : 0;
  const referenceValid   = !referenceRequired || reference.trim().length >= minRefLen;
  const referenceComplete = referenceValid;
  // Part 4.4 · pay_online is a special: no manual reference required,
  // the tender is created with status "pending" and settles the order
  // automatically when the guest confirms.
  const isPayOnline = mode === "pay_online";

  // Snapshot payments that already existed when the overlay opened.
  // Cancel reverses anything added during THIS session, not before.
  const priorPaymentIdsRef = useRef(new Set());
  // Sprint 3B · in the LIVE path, a reference-required tender is
  // staged locally until the cashier types a valid reference. The
  // staged payload is then POSTed to /pos/orders/{id}/tenders with
  // reference included, so the server never sees an empty reference.
  const pendingLivePayloadRef = useRef(null);
  useEffect(() => {
    if (open) {
      priorPaymentIdsRef.current = new Set(active.payments.map((p) => p.id));
      // Part 1 (Sprint 3) · pre-fill the amount field with the running
      // outstanding balance. Pressing Enter tenders the entire balance;
      // typing overrides the pre-fill for partials and split tenders.
      setAmount(String(Math.max(0, totals.balance).toFixed(2)));
      setMode("cash");
      setReference("");
      setExpanded(true);
      // Focus the container so keystrokes flow to our handler.
      setTimeout(() => containerRef.current?.focus(), 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Part 9 · when the mode changes to one that no longer needs a
  // reference, clear the field to avoid stale values leaking onto the
  // next payment.
  useEffect(() => {
    if (!referenceRequired) setReference("");
  }, [mode, referenceRequired]);

  const paid    = totals.paid;
  const balance = totals.balance;
  const change  = Math.max(0, -balance);
  const settled = balance <= 0.005;   // fully tendered
  const isSettlementComplete = active.order.state === ORDER_STATE.SETTLED;

  // Sprint 4 Part 5 · complimentary settlement writes revenue off
  // entirely and requires manager authorisation. Reason + authoriser
  // flow onto the order and, via the tender's reference field, into
  // the exception report. When requestAuth is not supplied (open-only
  // preview scenarios) the flow degrades gracefully to a direct
  // tender — but the production wiring in BillingScreen always
  // provides it.
  const gateCompIfNeeded = (amountToTender, onGo) => {
    if (mode !== "complimentary") { onGo({ auth: null }); return; }
    if (!requestAuth) { onGo({ auth: null }); return; }
    requestAuth(
      `Complimentary settlement · ₹${Number(amountToTender).toFixed(2)} written off`,
      (auth) => onGo({ auth }),
      "Reason for complimentary (required)"
    );
  };

  // Sprint 5 correction · Move focus to the reference input for the
  // mode of the tender just created. `setTimeout` gives React the tick
  // it needs to render the reference block if the mode just changed.
  const focusRefInput = () => {
    setTimeout(() => {
      const el = refInputRef.current;
      if (el) {
        el.focus();
        el.select?.();
      }
    }, 30);
  };
  // After a tender lands successfully, decide next step:
  // - If mode required a reference, save the tender id and jump focus
  //   to the reference field. Do NOT complete yet.
  // - Otherwise, if balance is zero, complete. If not, return focus to
  //   the mode list container for the next Enter.
  const advanceAfterTender = (createdTenderId, remainingBalance) => {
    if (referenceRequired) {
      setPendingRefTenderId(createdTenderId);
      focusRefInput();
      return;
    }
    if (remainingBalance <= 0.005) {
      // No reference needed and balance clear → complete right away.
      if (livePos) {
        livePos.settleOrder(active.order.id).then((r) => {
          if (r?.ok && change <= 0.005) onDone();
        });
      } else {
        dispatch({ type: ACT.ORDER_SETTLED });
        if (change <= 0.005) onDone();
      }
      return;
    }
    // Balance remaining, no reference to capture → return keyboard
    // focus to the mode list.
    setTimeout(() => containerRef.current?.focus(), 30);
  };

  const tenderExact = () => {
    if (settled) return;
    if (isPayOnline) return; // pay online routes through generatePayLink
    if (busyRef.current) return;
    const owed = Math.max(0, balance);
    if (!(owed > 0)) return;
    gateCompIfNeeded(owed, async ({ auth }) => {
      const tenderPayload = {
        mode, amount: owed,
        reference: reference.trim(),
        authorised_by: auth?.authorised_by,
        comp_reason:   auth?.reason_code || (mode === "complimentary" ? "MANAGER_DISCRETION" : undefined),
      };
      // Sprint 3B · reference-required modes in the LIVE path:
      // do NOT POST until the cashier has typed a valid reference,
      // otherwise the server refuses with a 400 and the reference
      // input is never rendered (bug found in iteration_335).
      if (livePos && referenceRequired && !referenceValid) {
        const stagedId = "staging_" + Math.random().toString(36).slice(2, 8);
        pendingLivePayloadRef.current = { staged_id: stagedId, payload: tenderPayload };
        setPendingRefTenderId(stagedId);
        focusRefInput();
        return;
      }
      if (livePos) {
        await withLock(async () => {
          const r = await livePos.addTender(active.order.id, tenderPayload);
          if (!r?.ok) return;
          if (!referenceRequired) setReference("");
          // Server decides when to settle — call it if balance is zero.
          if ((r.balance_due || 0) <= 0.005) {
            const sr = await livePos.settleOrder(active.order.id);
            if (sr?.ok && change <= 0.005) onDone();
            return;
          }
          setTimeout(() => containerRef.current?.focus(), 30);
        });
        return;
      }
      // Demo mode fallback — local reducer only.
      const createdId = "pay_" + Math.random().toString(36).slice(2, 8);
      dispatch({ type: ACT.PAYMENT_TENDERED, payload: {
        id: createdId, ...tenderPayload,
      }});
      if (!referenceRequired) setReference("");
      advanceAfterTender(createdId, Math.max(0, balance - owed));
    });
  };
  const tenderTyped = () => {
    if (isPayOnline) return;
    if (busyRef.current) return;
    const n = Number(amount);
    if (!(n > 0)) return;
    gateCompIfNeeded(n, async ({ auth }) => {
      // Financial Ground Truth (Feb 2026) · cash overpayment.
      // When the cashier types MORE than the outstanding balance in
      // cash, the server-side "amount applied to the invoice" is
      // capped at the balance; the cashier still records what was
      // physically taken (`amount_received`) and the server derives
      // `change_due = amount_received − amount_applied`. Non-cash
      // modes have `amount_received === amount_applied` and are
      // refused above the balance by the server (Part 6).
      const isCash = mode === "cash";
      const owed = Math.max(0, balance);
      const amountApplied = isCash ? Math.min(n, owed) : n;
      const tenderPayload = {
        mode,
        amount: amountApplied,
        amount_received: n,
        reference: reference.trim(),
        authorised_by: auth?.authorised_by,
        comp_reason:   auth?.reason_code || (mode === "complimentary" ? "MANAGER_DISCRETION" : undefined),
      };
      // Sprint 3B · defer the POST until reference is valid — see tenderExact.
      if (livePos && referenceRequired && !referenceValid) {
        const stagedId = "staging_" + Math.random().toString(36).slice(2, 8);
        pendingLivePayloadRef.current = { staged_id: stagedId, payload: tenderPayload };
        setPendingRefTenderId(stagedId);
        focusRefInput();
        return;
      }
      if (livePos) {
        await withLock(async () => {
          const r = await livePos.addTender(active.order.id, tenderPayload);
          if (!r?.ok) return;
          const remaining = r.balance_due || 0;
          setAmount(remaining > 0.005 ? remaining.toFixed(2) : "");
          if (!referenceRequired) setReference("");
          if (remaining <= 0.005) {
            const sr = await livePos.settleOrder(active.order.id);
            if (sr?.ok && change <= 0.005) onDone();
            return;
          }
          setTimeout(() => containerRef.current?.focus(), 30);
        });
        return;
      }
      const createdId = "pay_" + Math.random().toString(36).slice(2, 8);
      dispatch({ type: ACT.PAYMENT_TENDERED, payload: {
        id: createdId, ...tenderPayload,
      }});
      const remaining = Math.max(0, balance - amountApplied);
      setAmount(remaining > 0.005 ? remaining.toFixed(2) : "");
      if (!referenceRequired) setReference("");
      advanceAfterTender(createdId, remaining);
    });
  };

  // Sprint 5 correction · Enter in the reference input.
  // Attaches the typed reference to the pending tender, then either
  // completes (balance zero) or returns focus to the mode list.
  const acceptReference = async () => {
    if (!referenceValid) return;
    if (busyRef.current) return;
    const ref = reference.trim();
    // Sprint 3B · a staged live tender (pendingLivePayloadRef) means
    // the POST was deferred until we had a valid reference — now we
    // send it, reference and all, in ONE server round-trip.
    if (livePos && pendingLivePayloadRef.current &&
        pendingLivePayloadRef.current.staged_id === pendingRefTenderId) {
      const payload = { ...pendingLivePayloadRef.current.payload, reference: ref };
      pendingLivePayloadRef.current = null;
      setPendingRefTenderId(null);
      await withLock(async () => {
        const r = await livePos.addTender(active.order.id, payload);
        // Blur to avoid double-enter re-triggering
        refInputRef.current?.blur();
        if (!r?.ok) {
          // Server refused — keep the reference so the cashier can fix it.
          return;
        }
        setReference("");
        const remaining = r.balance_due || 0;
        setAmount(remaining > 0.005 ? remaining.toFixed(2) : "");
        if (remaining <= 0.005) {
          const sr = await livePos.settleOrder(active.order.id);
          if (sr?.ok && change <= 0.005) onDone();
          return;
        }
        setTimeout(() => containerRef.current?.focus(), 30);
      });
      return;
    }
    // Demo-mode / legacy path: attach the ref to a locally-created tender.
    if (pendingRefTenderId) {
      dispatch({ type: ACT.PAYMENT_REFERENCE_UPDATED, payload: {
        payment_id: pendingRefTenderId,
        reference:  ref,
      }});
    }
    setPendingRefTenderId(null);
    setReference("");
    refInputRef.current?.blur();
    if (balance <= 0.005) {
      if (livePos) {
        livePos.settleOrder(active.order.id).then((r) => {
          if (r?.ok && change <= 0.005) onDone();
        });
      } else {
        dispatch({ type: ACT.ORDER_SETTLED });
        if (change <= 0.005) onDone();
      }
      return;
    }
    // Balance remaining → focus back on the mode list container.
    setTimeout(() => containerRef.current?.focus(), 30);
  };

  // Part 4.4 · guest-paid online link UI shell.
  const generatePayLink = () => {
    if (isSettlementComplete) return;
    const owed = Math.max(0, balance);
    if (!(owed > 0)) return;
    // Mock reference — real system returns a URL / QR from the PSP.
    const ref = "PAY-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    dispatch({ type: ACT.PAYMENT_TENDERED, payload: {
      mode: "pay_online", amount: owed, reference: ref, status: "pending"
    }});
  };
  // Cancel the most recent pending pay_online tender (Part 4.4).
  const cancelPendingTender = () => {
    const pending = [...active.payments].reverse().find((p) => p.status === "pending");
    if (!pending) return;
    dispatch({ type: ACT.PAYMENT_REVERSED, payload: { payment_id: pending.id } });
  };
  // Simulate the PSP calling back with confirmation. Marks the pending
  // tender confirmed and — because no cashier is present to hit
  // Complete — auto-settles the order.
  const simulatePayOnlineConfirm = () => {
    const pending = [...active.payments].reverse().find((p) => p.status === "pending");
    if (!pending) return;
    dispatch({ type: ACT.PAYMENT_STATUS_UPDATED, payload: { payment_id: pending.id, status: "confirmed" } });
    // Auto-settle (Part 4.4 explicit exception): no cashier present.
    dispatch({ type: ACT.ORDER_SETTLED });
    if (onDone) setTimeout(onDone, 100);
  };

  const complete = async () => {
    if (!settled) return;
    if (busyRef.current) return;
    // Sprint 3B · server-side settlement finalises the order.
    if (livePos) {
      await withLock(async () => {
        const r = await livePos.settleOrder(active.order.id);
        if (r?.ok && change <= 0.005) onDone();
      });
      return;
    }
    dispatch({ type: ACT.ORDER_SETTLED });
    // Change-due path: state transitions to SETTLED and the render
    // switches to the settlement-complete panel. User will press Done.
    // Exact-amount path: no confirmation panel — close and orchestrate
    // immediately.
    if (change <= 0.005) {
      onDone();
    }
  };

  const cancelAndReverse = () => {
    if (isSettlementComplete) return; // Done is the only exit from complete panel
    // Clear a staged live tender that was never POSTed.
    pendingLivePayloadRef.current = null;
    setPendingRefTenderId(null);
    for (const p of active.payments) {
      if (!priorPaymentIdsRef.current.has(p.id)) {
        // Sprint 3B · reverse on the server if live, else demo path.
        if (livePos) livePos.reverseTender(p.id, { reason: "Cashier cancelled" });
        else dispatch({ type: ACT.PAYMENT_REVERSED, payload: { payment_id: p.id } });
      }
    }
    onCancel();
  };

  // Physical keyboard is our default interaction surface.
  //   Enter, Enter → tender exact + complete
  //   Digit         → expand and start manual amount entry
  //   Backspace     → delete last digit of amount
  //   Escape        → cancel + close (with reversal)
  //   Arrow ← / →   → cycle payment mode (compact prompt only)
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      // Financial Ground Truth (Feb 2026) · payment lock.
      // While a settlement request is in flight, ignore every key
      // that could produce another mutation. Escape stays live so
      // the cashier can cancel out; digits still buffer for the next
      // tender so a burst of typing while awaiting the network isn't
      // silently swallowed.
      if (busyRef.current) {
        if (e.key === "Enter") { e.preventDefault(); return; }
      }
      const k = e.key;

      if (isSettlementComplete) {
        if (k === "Enter") { e.preventDefault(); onDone(); }
        return;
      }

      if (k === "Escape") { e.preventDefault(); cancelAndReverse(); return; }
      if (k === "Enter")  {
        e.preventDefault();
        // Sprint 5 correction · Enter precedence:
        //   1. Balance zero AND every tender has its captured
        //      reference → complete the settlement.
        //   2. Amount typed → tender that amount to selected mode.
        //   3. Nothing typed → tender the full outstanding balance
        //      to selected mode.
        // After a tender, `advanceAfterTender` moves focus to the
        // reference field if one is required, or fires ORDER_SETTLED
        // itself when balance zero + no ref needed.
        const allReferencesCaptured = active.payments.every((p) => {
          const need = REFERENCE_LABEL[p.mode];
          const min  = MIN_REF_LEN[p.mode] || 1;
          if (!need) return true;
          return (p.reference || "").trim().length >= min;
        });
        if (settled && allReferencesCaptured) { complete(); return; }
        if (expanded && Number(amount) > 0) { tenderTyped(); return; }
        tenderExact();
        return;
      }
      if (/^[0-9]$/.test(k)) {
        e.preventDefault();
        setExpanded(true);
        setAmount((v) => (v.length >= 8 ? v : v + k));
        return;
      }
      if (k === ".") {
        e.preventDefault();
        setExpanded(true);
        setAmount((v) => (v.includes(".") ? v : (v || "0") + "."));
        return;
      }
      if (k === "Backspace") {
        e.preventDefault();
        setAmount((v) => v.slice(0, -1));
        return;
      }
      if (!expanded && (k === "ArrowRight" || k === "ArrowLeft" || k === "ArrowDown" || k === "ArrowUp")) {
        e.preventDefault();
        const dir = (k === "ArrowRight" || k === "ArrowDown") ? 1 : -1;
        const i   = MODES.findIndex((m) => m.id === mode);
        const j   = (i + dir + MODES.length) % MODES.length;
        setMode(MODES[j].id);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expanded, amount, mode, settled, isSettlementComplete]);

  // When settled while still on the tender view (before Complete),
  // the container keeps focus so Enter fires Complete. When we flip
  // into the settlement-complete panel, Done takes focus.
  useEffect(() => {
    if (!open) return;
    if (isSettlementComplete) doneRef.current?.focus();
    else                       containerRef.current?.focus();
  }, [open, settled, isSettlementComplete]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cancelAndReverse(); }}>
      <DialogContent
        data-testid="pos-payment-overlay"
        data-phase={isSettlementComplete ? "settlement-complete" : (expanded ? "manual" : "prompt")}
        // Prompt-class dialog: capped near 40% of a 1366×768 viewport.
        // The settlement-complete panel is a shade taller because of
        // the change-due hero; still bounded and never full-screen.
        className={`p-0 gap-0 rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] bg-white outline-none flex flex-col
          ${isSettlementComplete
            ? "max-w-[720px] w-[92vw] max-h-[92vh]"
            : (expanded ? "max-w-[640px] w-[92vw] max-h-[92vh]" : "max-w-[560px] w-[92vw] max-h-[92vh]")}
          overflow-hidden`}
        onEscapeKeyDown={(e) => { e.preventDefault(); if (!isSettlementComplete) cancelAndReverse(); }}
        onPointerDownOutside={(e) => { if (isSettlementComplete) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isSettlementComplete) e.preventDefault(); }}>
        <DialogHeader className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)] flex-row items-center justify-between space-y-0 shrink-0">
          <div className="min-w-0">
            <DialogTitle className="text-[12px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
              {isSettlementComplete ? "Settlement complete" : "Payment"}
            </DialogTitle>
            <DialogDescription className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] truncate">
              Order <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{active.order.id}</strong>
              {active.order.invoice_no
                ? <> · Invoice <strong className="text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">{active.order.invoice_no}</strong></>
                : null}
            </DialogDescription>
          </div>
          {!isSettlementComplete && (
            <button type="button" onClick={cancelAndReverse}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"
              data-testid="pos-payment-cancel">
              <XIcon className="w-3 h-3" /> Esc
            </button>
          )}
        </DialogHeader>

        {isSettlementComplete
          ? <SettlementCompletePanel
              order={active.order}
              totals={totals}
              payments={active.payments}
              change={change}
              onDone={onDone}
              doneRef={doneRef} />
          : <TenderPromptPanel
              containerRef={containerRef}
              totals={totals} payments={active.payments}
              mode={mode} setMode={setMode}
              amount={amount} setAmount={setAmount}
              expanded={expanded} setExpanded={setExpanded}
              settled={settled} balance={balance} change={change}
              reference={reference} setReference={setReference}
              referenceRequired={referenceRequired}
              referenceLabel={referenceLabel}
              isPayOnline={isPayOnline}
              onGeneratePayLink={generatePayLink}
              onSimulatePayOnlineConfirm={simulatePayOnlineConfirm}
              onCancelPending={cancelPendingTender}
              referenceComplete={referenceComplete}
              referenceValid={referenceValid}
              minRefLen={minRefLen}
              refInputRef={refInputRef}
              onAcceptReference={acceptReference}
              pendingRefTenderId={pendingRefTenderId}
              allReferencesCaptured={active.payments.every((p) => {
                const need = REFERENCE_LABEL[p.mode];
                const min  = MIN_REF_LEN[p.mode] || 1;
                if (!need) return true;
                return (p.reference || "").trim().length >= min;
              })}
              onTenderExact={tenderExact}
              onTenderTyped={tenderTyped}
              onComplete={complete}
              busy={busy}
              dispatch={dispatch} />
        }
      </DialogContent>
    </Dialog>
  );
}

// ─── Compact tender-prompt panel ────────────────────────────────────
function TenderPromptPanel({
  containerRef, totals, payments,
  mode, setMode, amount, setAmount, expanded, setExpanded,
  settled, balance, change,
  reference, setReference, referenceRequired, referenceLabel, referenceComplete,
  referenceValid = true, minRefLen = 0, refInputRef, onAcceptReference,
  pendingRefTenderId,
  allReferencesCaptured = true,
  isPayOnline, onGeneratePayLink, onSimulatePayOnlineConfirm, onCancelPending,
  onTenderExact, onTenderTyped, onComplete, busy = false, dispatch,
}) {
  const owed = Math.max(0, balance);
  const selectedMode = MODES.find((m) => m.id === mode) || MODES[0];
  const pendingTender = [...payments].reverse().find((p) => p.status === "pending");
  const completeCtaRef = useRef(null);
  // Sprint 5 correction · Complete-payment CTA focuses the moment
  // balance hits zero AND every recorded tender has captured its
  // reference. Individual field validity of the CURRENT reference
  // input no longer gates the CTA — an already-captured reference
  // on a prior tender is enough.
  useEffect(() => {
    if (settled && allReferencesCaptured) {
      const t = setTimeout(() => completeCtaRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [settled, allReferencesCaptured]);

  return (
    <div ref={containerRef} tabIndex={0} className="flex flex-col outline-none flex-1 min-h-0"
      data-testid="pos-payment-prompt">
      {/* Scrollable middle so the CTA at the bottom is always in view */}
      <div className="flex-1 min-h-0 overflow-y-auto">
      {/* Payable / running Balance row. Balance is prominent only when
          it differs from Payable, keeping the compact form calm. */}
      <div className="px-4 pt-3 pb-2 flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Payable</div>
          <div className="text-[36px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums leading-none mt-0.5"
            data-testid="pos-money-payable">₹{fmt(totals.grand_total)}</div>
        </div>
        {payments.length > 0 && (
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Balance</div>
            <div className={`text-[22px] font-medium tabular-nums leading-none mt-0.5
                            ${settled ? "text-emerald-800" : "text-amber-800"}`}
              data-testid="pos-money-balance">
              ₹{fmt(Math.max(0, balance))}
            </div>
          </div>
        )}
      </div>

      {/* Sprint 6 correction 2 · vertical mode list. Every mode is
          visible at once; each row carries its own amount field on
          the same line as the label + already-tendered chip. Tapping
          or tabbing into a row's amount field focuses it AND selects
          that mode. Enter in the amount field tenders. If the mode
          needs a reference, focus jumps to the row-local reference
          input; Enter there attaches the ref. Pre-fill: the first
          empty field is pre-filled with the outstanding balance so a
          single-mode settlement is one keystroke. */}
      <div className="px-3 pb-2 space-y-1.5" data-testid="pos-modes-vertical">
        {MODES.map((m, mIdx) => {
          const rowActive  = m.id === mode;
          const rowTenders = payments.filter((p) => p.mode === m.id);
          const rowPaid    = rowTenders.reduce((s, p) => s + Number(p.amount || 0), 0);
          const isPO       = m.id === "pay_online";
          const rowRefLabel = REFERENCE_LABEL[m.id];
          const rowMinRef   = rowRefLabel ? (MIN_REF_LEN[m.id] || 1) : 0;
          // First empty field (no payments yet on this mode AND
          // outstanding balance > 0 AND no other row further up has
          // already been chosen as the prefill target): shows the
          // outstanding balance as the placeholder.
          const isPrefillTarget = mIdx === 0 && payments.length === 0 && owed > 0;
          const rowValue = rowActive ? amount : "";
          return (
            <div key={m.id}
              data-testid={`pos-mode-row-${m.id}`}
              data-active={rowActive ? "1" : "0"}
              className={`rounded-lg border transition
                ${rowActive
                  ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary-tint,#E7EEEB)]/40"
                  : rowPaid > 0
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white"}`}>
              <div className="flex items-center gap-2 px-2.5 py-2">
                <button type="button" onClick={() => setMode(m.id)}
                  data-testid={`pos-mode-${m.id}`}
                  className="inline-flex items-center gap-1.5 min-w-[132px] shrink-0 h-8 px-2 rounded text-[12px] font-medium text-left"
                  title={m.label}>
                  <m.icon className="w-3.5 h-3.5" />
                  <span className="truncate">{m.label}</span>
                </button>
                {isPO ? (
                  <div className="flex-1 text-[10px] text-sky-800 italic">
                    Online link · use the panel below
                  </div>
                ) : (
                  <input type="text" inputMode="decimal"
                    value={rowValue}
                    placeholder={isPrefillTarget && !rowActive
                      ? `Balance ₹${fmt(owed)}`
                      : "₹0.00"}
                    onFocus={() => {
                      // Sprint 6 correction 2 · focusing a row's
                      // amount SELECTS its mode and CLEARS whatever
                      // amount was left over from the previous
                      // tender (or previously-active row). The
                      // outstanding balance is placed in the field
                      // ONLY when this is the first-empty prefill
                      // target — the very first tender of the bill.
                      // Otherwise a leftover "850.00" prefill would
                      // concatenate with the cashier's typed "500"
                      // and produce a nonsense ₹850500.
                      setMode(m.id);
                      if (isPrefillTarget && payments.length === 0) {
                        setAmount(owed.toFixed(2));
                      } else if (m.id !== mode) {
                        setAmount("");
                      }
                    }}
                    onChange={(e) => {
                      setMode(m.id);
                      setAmount(e.target.value.replace(/[^0-9.]/g, ""));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setMode(m.id);
                        const n = Number(amount);
                        if (n > 0) onTenderTyped();
                        else onTenderExact();
                      }
                    }}
                    className={`flex-1 h-9 rounded border px-2 text-[14px] tabular-nums text-right focus:outline-none
                      ${rowActive
                        ? "border-[color:var(--color-primary)] bg-white"
                        : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white"}`}
                    data-testid={`pos-mode-${m.id}-amount`} />
                )}
                {rowPaid > 0 && (
                  <div className="text-right shrink-0 min-w-[92px]">
                    <div className="text-[9px] uppercase tracking-widest text-emerald-800 font-semibold">Tendered</div>
                    <div className="text-[13px] font-semibold tabular-nums text-emerald-900"
                      data-testid={`pos-mode-${m.id}-paid`}>₹{fmt(rowPaid)}</div>
                  </div>
                )}
              </div>
              {/* Row-local reference input — appears only for the
                  active row while a tender on this mode is awaiting
                  its reference. */}
              {rowActive && rowRefLabel && !isPO && pendingRefTenderId && (
                <div className="px-2.5 pb-2" data-testid={`pos-mode-${m.id}-ref-block`}>
                  <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium mb-1">
                    {rowRefLabel} · required{rowMinRef > 1 ? ` · minimum ${rowMinRef} characters` : ""}
                  </div>
                  <input ref={refInputRef} type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (referenceValid && onAcceptReference) onAcceptReference();
                      }
                      if (e.key === "Escape") { e.preventDefault(); refInputRef?.current?.blur(); }
                    }}
                    placeholder={`${rowRefLabel} · min ${rowMinRef} chars`}
                    className={`w-full h-9 rounded border px-2 text-[12px] tabular-nums focus:outline-none
                      ${referenceValid
                        ? "border-emerald-500 bg-white"
                        : "border-amber-500 bg-amber-50"}`}
                    data-testid="pos-reference-input" />
                  {!referenceValid && (
                    <div className="mt-1 text-[10px] text-amber-800" data-testid="pos-reference-hint">
                      Type at least {rowMinRef} characters. Enter accepts a valid entry.
                    </div>
                  )}
                </div>
              )}
              {/* Individual tenders for this row (chips with reverse) */}
              {rowTenders.length > 0 && (
                <div className="px-2.5 pb-2 flex flex-wrap gap-1"
                  data-testid={`pos-mode-${m.id}-chips`}>
                  {rowTenders.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-300 px-2 py-0.5 text-[10px] text-emerald-900"
                      data-testid={`pos-payment-${p.id}`}>
                      ₹{fmt(p.amount)}
                      {p.reference && (
                        <span className="text-[9px] text-emerald-700"
                          data-testid={`pos-payment-${p.id}-ref`}>· {p.reference}</span>
                      )}
                      <button type="button"
                        onClick={() => dispatch({ type: ACT.PAYMENT_REVERSED, payload: { payment_id: p.id } })}
                        data-testid={`pos-payment-${p.id}-reverse`}
                        aria-label="Reverse"
                        className="opacity-60 hover:opacity-100">
                        <XIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Running totals — Total tendered + Outstanding balance. */}
      <div className="px-4 py-2 border-t border-b border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] flex items-center justify-between"
        data-testid="pos-running-totals">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Total tendered</div>
          <div className="text-[16px] font-semibold tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]"
            data-testid="pos-running-tendered">
            ₹{fmt(payments.reduce((s, p) => s + Number(p.amount || 0), 0))}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Outstanding</div>
          <div className={`text-[16px] font-semibold tabular-nums
            ${owed > 0 ? "text-amber-800" : "text-emerald-800"}`}
            data-testid="pos-running-outstanding">
            ₹{fmt(Math.max(0, owed))}
          </div>
        </div>
      </div>

      {/* Part 4.4 · Pay online — pending tender panel. Guest-paid link
          simulated in-app; no gateway calls. The order stays open and
          unsettled while the tender is pending; on the simulated
          confirm the order auto-settles because no cashier is present. */}
      {isPayOnline && (
        <div className="px-4 pb-2" data-testid="pos-pay-online-block">
          {!pendingTender && (
            <div className="rounded border border-sky-200 bg-sky-50 p-3 flex items-center justify-between gap-3">
              <div className="text-[11px] text-sky-900 leading-snug">
                Guest pays online via link or QR. Owed <strong className="tabular-nums">₹{fmt(owed)}</strong>.
                <div className="text-[10px] text-sky-800 opacity-80 mt-0.5">
                  Tender is created as pending; order auto-settles on confirmation.
                </div>
              </div>
              <button type="button" onClick={onGeneratePayLink}
                disabled={!(owed > 0)}
                data-testid="pos-pay-online-generate"
                className="h-10 px-3 rounded bg-sky-700 text-white text-[12px] font-semibold hover:bg-sky-800 disabled:opacity-40">
                Generate link
              </button>
            </div>
          )}
          {pendingTender && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 space-y-2"
              data-testid="pos-pay-online-pending">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold">
                  Awaiting guest confirmation
                </div>
                <button type="button" onClick={onCancelPending}
                  data-testid="pos-pay-online-cancel-pending"
                  className="h-7 px-2 rounded border border-amber-300 text-[10px] text-amber-900 hover:bg-amber-100">
                  Cancel pending
                </button>
              </div>
              <div className="flex items-center gap-3">
                {/* Placeholder QR — a filled square with the ref beneath */}
                <div className="w-24 h-24 bg-white border-2 border-amber-400 grid place-items-center rounded"
                  data-testid="pos-pay-online-qr">
                  <QrCode className="w-14 h-14 text-amber-900" />
                </div>
                <div className="text-[11px] text-amber-900 leading-snug">
                  <div className="text-[9px] uppercase tracking-widest text-amber-700 mb-0.5">Reference</div>
                  <div className="text-[15px] font-mono tabular-nums text-[color:var(--pos-color-primary-text,#1A2421)]"
                    data-testid="pos-pay-online-ref">{pendingTender.reference}</div>
                  <div className="mt-1">Amount <strong className="tabular-nums">₹{fmt(pendingTender.amount)}</strong></div>
                </div>
              </div>
              <button type="button" onClick={onSimulatePayOnlineConfirm}
                data-testid="pos-pay-online-simulate-confirm"
                className="w-full h-10 rounded bg-emerald-700 text-white text-[12px] font-semibold hover:bg-emerald-800 inline-flex items-center justify-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Simulate guest confirmation
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sprint 6 correction 2 · legacy manual amount panel + tender
          hint removed. Amount entry now lives in each row's inline
          field. The keypad-style manual panel was a discovery step
          that the vertical layout eliminates. */}
      {/* end scrollable middle */}
      </div>

      {/* Part 1 (Sprint 4 corrections) · PRIMARY Complete-payment CTA
          pinned as a sticky footer inside the dialog. Always visible,
          never inside the keypad. Disabled with the reason stated
          in-line when balance > 0 OR a required reference is missing.
          Enabled + focused the moment both conditions clear so Enter
          completes. Applies identically to cash / card / UPI / room /
          Swiggy / Zomato / Due. */}
      <div className="px-4 py-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] bg-white shrink-0" data-testid="pos-complete-cta-region"
        data-busy={busy ? "1" : "0"}>
        <button type="button"
          ref={completeCtaRef}
          onClick={onComplete}
          disabled={!settled || !allReferencesCaptured || busy}
          className={`w-full h-14 rounded-lg text-[16px] font-semibold transition inline-flex items-center justify-center gap-2
            ${busy
              ? "bg-[#5C6B64] text-white cursor-wait animate-pulse"
              : (settled && allReferencesCaptured)
                ? "bg-emerald-700 hover:bg-emerald-800 text-white ring-2 ring-emerald-300 shadow-md"
                : "bg-[#F0F0EE] text-[#8A8A83] cursor-not-allowed border border-[color:var(--pos-color-border,#E2E8DF)]"}`}
          data-testid="pos-complete-payment-primary">
          <Check className="w-5 h-5" />
          {busy
            ? <>Recording payment… <span className="opacity-80 text-[12px]">· please wait</span></>
            : settled && allReferencesCaptured
              ? <>Complete payment <span className="opacity-80 text-[12px]">· press Enter</span></>
              : !settled
                ? <>Complete payment · <span className="opacity-90 text-[12px]">₹{fmt(balance)} still due</span></>
                : <>Complete payment · <span className="opacity-90 text-[12px]">Reference required on one or more tenders</span></>
          }
        </button>
      </div>
    </div>
  );
}

function ManualAmountPanel({ amount, setAmount, selectedMode, owed, onTenderTyped, onCollapse }) {
  // Item 1 (Sprint 3 corrections) · Tender-key styling is now DEDICATED
  // (does not extend `cell`). Extending `cell` re-adds `bg-white
  // text-[color:var(--pos-color-primary-text,#1A2421)]` which then conflicts with the primary overrides
  // and — depending on Tailwind class order — produces white text on
  // a white fill. The dedicated class carries only the primary
  // palette so it is unambiguously readable.
  const cell = "h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[18px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]";
  const tenderCell = "h-11 rounded text-[18px] font-semibold tabular-nums border border-transparent bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1";
  const tap  = (k) => () => {
    if (k === "Backspace") setAmount((v) => v.slice(0, -1));
    else if (k === ".")    setAmount((v) => (v.includes(".") ? v : (v || "0") + "."));
    else                   setAmount((v) => (v.length >= 8 ? v : v + k));
  };
  return (
    <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] px-3 py-2" data-testid="pos-manual-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium flex-1">
          Amount in {selectedMode.label}
        </div>
        <button type="button" onClick={onCollapse}
          className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:text-[color:var(--pos-color-primary-text,#1A2421)] underline underline-offset-2"
          data-testid="pos-manual-collapse">
          Back to exact ₹{fmt(owed)}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder="0"
          className="flex-1 h-12 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[28px] font-medium tabular-nums px-3 focus:outline-none focus:border-[color:var(--pos-color-accent,var(--color-primary))]"
          data-testid="pos-amount-input" />
        {selectedMode.allowsQuick && (
          <div className="flex items-center gap-1">
            {QUICK_CASH.map((q) => (
              <button key={q} type="button" onClick={() => setAmount(String(q))}
                data-testid={`pos-quick-${q}`}
                className="h-12 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[11px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[#F0F0EE] tabular-nums">
                ₹{q}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1">
        {["1","2","3"].map((d) => <button key={d} type="button" onClick={tap(d)} className={cell} data-testid={`pos-key-${d}`}>{d}</button>)}
        <button type="button" onClick={tap("Backspace")} className={cell + " text-[#7A2E1F]"} data-testid="pos-key-back"><Delete className="inline w-4 h-4" /></button>
        {["4","5","6"].map((d) => <button key={d} type="button" onClick={tap(d)} className={cell} data-testid={`pos-key-${d}`}>{d}</button>)}
        <button type="button" onClick={onTenderTyped} disabled={!(Number(amount) > 0)}
          className={tenderCell}
          data-testid="pos-tender">Tender</button>
        {["7","8","9"].map((d) => <button key={d} type="button" onClick={tap(d)} className={cell} data-testid={`pos-key-${d}`}>{d}</button>)}
        <div />
        <button type="button" onClick={tap(".")} className={cell} data-testid="pos-key-dot">.</button>
        <button type="button" onClick={tap("0")} className={cell + " col-span-2"} data-testid="pos-key-0">0</button>
      </div>
    </div>
  );
}

// ─── Settlement-complete panel — unchanged behaviour ────────────────
function SettlementCompletePanel({ order, totals, payments, change, onDone, doneRef }) {
  // Financial Ground Truth (Feb 2026) · prefer the server-authoritative
  // change_due carried on each tender. Falls back to the legacy local
  // subtraction (`Math.max(0, -balance)`) only when tenders haven't
  // yet been rehydrated with the new fields (demo mode, older cache).
  const serverChange = (payments || []).reduce(
    (s, p) => s + (Number(p.change_due) || 0), 0,
  );
  const cashReceived = (payments || [])
    .filter((p) => p.mode === "cash")
    .reduce((s, p) => s + (Number(p.amount_received) || Number(p.amount) || 0), 0);
  const totalApplied = (payments || []).reduce(
    (s, p) => s + (Number(p.amount) || 0), 0,
  );
  const changeToShow = serverChange > 0.005 ? serverChange : change;
  const hasChange = changeToShow > 0.005;
  return (
    <div className="flex flex-col" data-testid="pos-settlement-complete">
      <div className="flex items-center gap-2 bg-emerald-50 border-b border-emerald-200 px-4 py-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
        <div className="text-[11px] text-emerald-900">
          Invoice <strong className="tabular-nums" data-testid="pos-settled-invoice-no">{order.invoice_no}</strong>
          {" · Grand total "}
          <strong className="tabular-nums" data-testid="pos-settled-grand-total">₹{fmt(totals.grand_total)}</strong>
        </div>
      </div>

      {hasChange ? (
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-4 bg-emerald-50">
          <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-900 font-medium">Change to return</div>
          <div className="text-[72px] font-medium text-emerald-800 tabular-nums leading-none mt-1"
            data-testid="pos-change-due-amount">₹{fmt(changeToShow)}</div>
          {/* Show the explicit cash-received / amount-applied breakdown
              from the server tender. Matches the printed receipt. */}
          <div className="mt-3 w-full max-w-xs grid grid-cols-2 gap-y-1 text-[12px] text-emerald-900">
            <span>Cash received</span>
            <strong className="tabular-nums text-right" data-testid="pos-cash-received">
              ₹{fmt(cashReceived)}
            </strong>
            <span>Amount applied</span>
            <strong className="tabular-nums text-right" data-testid="pos-amount-applied">
              ₹{fmt(totalApplied)}
            </strong>
            <span>Change due</span>
            <strong className="tabular-nums text-right" data-testid="pos-change-due-inline">
              ₹{fmt(changeToShow)}
            </strong>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-4" data-testid="pos-no-change-note">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Exact amount</div>
          <div className="text-[28px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums mt-1">No change due</div>
        </div>
      )}

      <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] bg-[color:var(--pos-color-raised-surface,#F9F9F8)] px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto"
        data-testid="pos-settled-tender-list">
        <span className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium shrink-0">Tenders</span>
        {payments.map((p, idx) => {
          const meta = MODES.find((m) => m.id === p.mode);
          return (
            <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--pos-color-border,#E2E8DF)] bg-white px-2 py-0.5 text-[10px] shrink-0"
              data-testid={`pos-settled-tender-${idx}`}>
              {meta?.label || p.mode} <strong className="tabular-nums">₹{fmt(p.amount)}</strong>
            </span>
          );
        })}
      </div>

      <div className="border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 py-2 flex items-center justify-end">
        <button type="button" ref={doneRef} onClick={onDone}
          className="h-10 px-6 rounded bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-[13px] font-semibold"
          data-testid="pos-settlement-done">
          Done <span className="text-[10px] font-normal opacity-80 ml-1">· ↵</span>
        </button>
      </div>
    </div>
  );
}
